import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../../../app/hooks';
import { EMAIL, NAME, PHONE, TRIGGER_NOTIFICATION, JOB_ROLE } from '../../../../../utils/constants/companyLogin';
import { CountryCodeData } from '../../../../Company/CompanyLogin/CountryCodesDropdown/CountryCodesDropdown';
import { DropdownOption } from '../../../../../common/_custom/Dropdown/dropdownTypes';
import { DEFAULT_JOB_ROLES } from './AddUserModal';
import { editUserDetailsV2 } from '../../../../../store/investor/apiHelpers';
import { EditUserV2Payload } from '../../../../../store/investor/service';
import { toast } from 'react-toastify';
import { setEditUserModal } from '../../../../../store/investor/action';
import { emailRegex, extractPhoneNumber } from '../../../../../utils/utils';
import { Mixpanel } from '../../../../../utils/mixpanel';
import { USER_TYPE_OPTIONS, USER_ROLE_KEY, DEFAULT_TEAM_ROLE, SUPER_ADMIN_ROLE, RoleOption } from '../../ManageUserConstants';

interface UseEditUserModalProps {
    isOpen: boolean;
    userData: any;
    allRowsData?: any;
    onUsersUpdate?: (updatedUsers: any[]) => void;
    countryCodes: CountryCodeData[];
    onClose: () => void;
    isCompanySide: boolean;
    canEditRole: boolean;
    teamRoleOptions?: DropdownOption[];
    defaultTeamRoleId?: string;
    allowUserTypeEdit?: boolean;
}

export const useEditUserModal = ({
    isOpen,
    userData,
    allRowsData,
    onUsersUpdate,
    countryCodes,
    onClose,
    isCompanySide,
    canEditRole,
    teamRoleOptions = USER_TYPE_OPTIONS,
    defaultTeamRoleId = DEFAULT_TEAM_ROLE,
    allowUserTypeEdit = true,
}: UseEditUserModalProps) => {
    const dispatch = useDispatch();
    const loggedInUser = useAppSelector((state) => state.user.userData);

    const [loading, setLoading] = useState(false);
    const [selectedOrgData, setSelectedOrgData] = useState<any>(allRowsData || []);
    const [showDemotionConfirm, setShowDemotionConfirm] = useState(false);

    const [formData, setFormData] = useState<any>({
        [NAME]: '',
        [EMAIL]: '',
        [PHONE]: '',
        [JOB_ROLE]: null,
        [TRIGGER_NOTIFICATION]: false,
        [USER_ROLE_KEY]: null as DropdownOption | null,
    });

    const [formErrors, setFormErrors] = useState({
        [NAME]: '',
        [EMAIL]: '',
        [PHONE]: '',
    });

    // Initialize form data when modal opens
    useEffect(() => {
        if (!isOpen || !userData || !countryCodes?.length) {
            return;
        }

        // Extract phone number from mobileNumber
        const mobileNumber = userData.mobileNumber || '';
        const { phoneNumber } = extractPhoneNumber(mobileNumber, userData.countryCode, countryCodes);

        // Extract job role
        let jobRole: DropdownOption | null = null;
        const jobRoleLabel = userData.customFields?.jobRole;
        if (jobRoleLabel) {
            jobRole =
                DEFAULT_JOB_ROLES.find((role) => role.label === jobRoleLabel) ??
                ({ id: jobRoleLabel, label: jobRoleLabel } as DropdownOption);
        }

        // Preselect role dropdown:
        // 1. userData.role exact match by id in teamRoleOptions
        // 2. userData.userType label match (persona label e.g. "Admin" → viewName match)
        // 3. null — do NOT force defaultTeamRoleId
        // Runs for both company-side (Manage Team) and non-company-side (Manage Users/Team tab)
        let teamRoleOption: DropdownOption | null = null;
        if (userData.role) {
            teamRoleOption = teamRoleOptions.find((o) => o.id === userData.role) ?? null;
        }
        if (!teamRoleOption && userData.userType) {
            teamRoleOption = teamRoleOptions.find((o) => o.label === userData.userType) ?? null;
        }

        setFormData({
            [NAME]: userData.name || '',
            [EMAIL]: userData.emailId || '',
            [PHONE]: phoneNumber,
            [JOB_ROLE]: jobRole,
            [TRIGGER_NOTIFICATION]: false,
            [USER_ROLE_KEY]: teamRoleOption,
        });
    }, [isOpen, userData, countryCodes, isCompanySide]);

    // Update selectedOrgData when allRowsData changes
    useEffect(() => {
        if (!Array.isArray(allRowsData)) {
            return;
        }
        setSelectedOrgData(allRowsData);
    }, [allRowsData]);

    // Update selectedOrgData when formData changes
    useEffect(() => {
        if (!userData?.userId) {
            return;
        }

        setSelectedOrgData((prevData: any[]) => {
            if (!Array.isArray(prevData) || prevData.length === 0) {
                return prevData;
            }

            const userIndex = prevData.findIndex((user: any) => user.userId === userData.userId);
            if (userIndex === -1) {
                return prevData;
            }

            const countryCodeInfo = countryCodes?.find((cc) => cc.countryCode === userData.countryCode);
            const phoneNumber = formData[PHONE] || '';
            const mobileNumber = phoneNumber && countryCodeInfo ? `+${countryCodeInfo.callingCode} ${phoneNumber}` : phoneNumber || '';

            const isEmailChanged = formData[EMAIL]?.trim() !== allRowsData[userIndex]?.emailId?.trim();

            // Reflect the selected role/userType so the User Type column updates instantly after save
            // (column resolves roleCatalog[row.role].viewName; keep prior value when no role is selected).
            const selectedRole = formData[USER_ROLE_KEY] as RoleOption | null;

            const updatedData = [...prevData];
            updatedData[userIndex] = {
                ...updatedData[userIndex],
                name: formData[NAME] || updatedData[userIndex].name,
                emailId: formData[EMAIL] || updatedData[userIndex].emailId,
                mobileNumber: mobileNumber,
                userStatus: isEmailChanged ? 'Unverified' : allRowsData[userIndex]?.userStatus,
                role: allowUserTypeEdit ? selectedRole?.id ?? updatedData[userIndex].role : updatedData[userIndex].role,
                userType: allowUserTypeEdit ? selectedRole?.userType ?? updatedData[userIndex].userType : updatedData[userIndex].userType,
                customFields: {
                    ...updatedData[userIndex].customFields,
                    jobRole: formData[JOB_ROLE]?.label ?? updatedData[userIndex].customFields?.jobRole ?? '',
                },
            };
            return updatedData;
        });
    }, [formData, userData?.userId, countryCodes]);

    const handleFormChange = (val: any, identifier: string) => {
        setFormData((data) => ({
            ...data,
            [identifier]: val,
        }));

        if (formErrors[identifier]) {
            setFormErrors((errors) => ({
                ...errors,
                [identifier]: '',
            }));
        }
    };

    const validateForm = (): boolean => {
        const errors = { [NAME]: '', [EMAIL]: '', [PHONE]: '' };
        let hasErrors = false;

        if (!formData[NAME]?.trim()) {
            errors[NAME] = 'Name is required';
            hasErrors = true;
        }

        const email = formData[EMAIL]?.trim();
        if (!email) {
            errors[EMAIL] = 'Email is required';
            hasErrors = true;
        } else if (!emailRegex.test(email)) {
            errors[EMAIL] = 'Enter a valid email address';
            hasErrors = true;
        }

        const phone = formData[PHONE]?.trim();
        if (phone && phone.length < 10) {
            errors[PHONE] = 'Enter a valid phone number';
            hasErrors = true;
        }

        setFormErrors(errors);
        return !hasErrors;
    };

    // Returns true if the logged-in user is editing their own row AND downgrading from SUPER_ADMIN on the Team side
    const isSelfDemotion = (): boolean => {
        if (isCompanySide) return false;
        if (!userData || !loggedInUser) return false;
        const isSelf =
            (userData.userId && userData.userId === loggedInUser.userId) ||
            (userData.emailId && userData.emailId === loggedInUser.primaryEmail);
        if (!isSelf) return false;
        const currentRole = userData.role ?? userData.userType;
        const selectedRole = formData[USER_ROLE_KEY]?.id;
        return !!selectedRole && currentRole === SUPER_ADMIN_ROLE && selectedRole !== SUPER_ADMIN_ROLE;
    };

    const executeSave = async (): Promise<void> => {
        if (!validateForm() || !userData?.userId) {
            return;
        }

        setLoading(true);
        try {
            const userDetails: EditUserV2Payload['userDetails'] = {
                userId: userData.userId,
                name: formData[NAME]?.trim() || '',
                emailId: formData[EMAIL]?.trim() || '',
                phoneNumber: formData[PHONE]?.trim() || '',
                customFields: {
                    jobRole: formData[JOB_ROLE]?.label || '',
                },
            };

            // Include role when dropdown was shown and has a value — applies to both company-side and non-company-side
            if (allowUserTypeEdit && canEditRole && formData[USER_ROLE_KEY]?.id) {
                userDetails.role = formData[USER_ROLE_KEY].id;
                if (formData[USER_ROLE_KEY].userType) {
                    userDetails.userType = formData[USER_ROLE_KEY].userType;
                }
            }

            const payload = {
                userDetails,
                triggerNotification: formData[TRIGGER_NOTIFICATION] ?? false,
            };

            const response = await editUserDetailsV2(payload);
            // editUserDetailsV2 returns {} on error (already shows toast) and the full data on success
            if (response?.responseData?.responseCode === 20) {
                toast.success('User updated successfully');
                onUsersUpdate?.(selectedOrgData);
                dispatch(setEditUserModal(false, null, true));
                Mixpanel.track('edited_user_type', {
                    user_email: formData[EMAIL],
                    current_url: window.location.href,
                    role_changed: Boolean(
                        canEditRole &&
                            ((userDetails.role && userDetails.role !== userData.role) ||
                                (userDetails.userType && userDetails.userType !== userData.userType)),
                    ),
                });
                resetForm();
            }
            // If response is {} (error), editUserDetailsV2 already surfaced the toast; do not close.
        } catch (error) {
            console.error('Failed to edit user:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (): Promise<void> => {
        if (!validateForm()) return;

        if (isSelfDemotion()) {
            setShowDemotionConfirm(true);
            return;
        }

        await executeSave();
    };

    const handleConfirmDemotion = async (): Promise<void> => {
        setShowDemotionConfirm(false);
        await executeSave();
    };

    const handleCancelDemotion = (): void => {
        setShowDemotionConfirm(false);
    };

    const resetForm = () => {
        setFormData({
            [NAME]: '',
            [EMAIL]: '',
            [PHONE]: '',
            [JOB_ROLE]: null,
            [TRIGGER_NOTIFICATION]: false,
            [USER_ROLE_KEY]: null,
        });
        setFormErrors({
            [NAME]: '',
            [EMAIL]: '',
            [PHONE]: '',
        });
        setSelectedOrgData([]);
        setShowDemotionConfirm(false);
    };

    const onClosedModal = () => {
        resetForm();
        onClose();
    };

    const isSaveDisabled = !formData[NAME]?.trim() || !formData[EMAIL]?.trim() || !emailRegex.test(formData[EMAIL] || '') || loading;

    return {
        loading,
        formData,
        formErrors,
        selectedOrgData,
        handleFormChange,
        handleSave,
        handleConfirmDemotion,
        handleCancelDemotion,
        onClosedModal,
        isSaveDisabled,
        showDemotionConfirm,
        USER_ROLE_KEY,
    };
};
